import React, { useEffect, useState } from 'react'
import i18n from 'simple-react-i18n'
import PropTypes from 'prop-types'
import { Button, Card, CardContent, Dialog, DialogActions, DialogContent, Grid2, Icon, Tooltip } from '@mui/material'
import { CardTable } from 'components/datatable/NewTable'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { SANDRE } from 'referencial/constants/ReferencialConstants'
import ToastrAction from 'toastr/actions/ToastrAction'
import useSandreList from 'utils/customHook/useSandreList'
import { compact } from 'lodash'
import { DefaultDialogTitle } from 'components/styled/Dialog'
import Select from 'components/forms/Select'
import { getSandreLabel } from 'utils/StringUtil'
import SimpleDatePicker from 'components/forms/SimpleDatePicker'
import { hasValue } from 'utils/NumberUtil'
import Input from 'components/forms/Input'
import SimpleTextArea from 'components/forms/SimpleTextArea'
import { getDate } from 'utils/DateUtil'
import { mediumGrey, PASTEL_COLOR } from 'utils/constants/ColorTheme'
import ToggleButtonGroup from 'components/buttons/ToggleButtonGroup'

const OTHERS = -1
const DOC_STATE = {
    EXISTING: '1',
    ABSENT: '2',
    NOTSPECIFIED: '3',
}

const DocumentDialog = ({
    selectedDocument = {},
    isOpen = false,
    setIsOpen = () => { },
    onChange = () => { },
}) => {
    const dispatch = useDispatch()

    const [doc, setDocument] = useState(selectedDocument)
    const documents = useSandreList(SANDRE.PGSSE_DOCUMENTS)
    const relevances = useSandreList(SANDRE.CODE_PERTINENCE)

    useEffect(()=> {
        setDocument(selectedDocument)
    }, [selectedDocument])

    const onChangeDocument = values => setDocument(prev => ({ ...prev, ...values }))

    return (
        <Dialog open={isOpen} maxWidth='lg'>
            <DefaultDialogTitle title={selectedDocument.index ? i18n.newDocument : i18n.editDocument} onClose={() => setIsOpen(false)} />
            <DialogContent>
                <Card>
                    <CardContent>
                        <Grid2 container spacing={1} alignItems='center'>
                            <Grid2 size={6}>
                                {
                                    doc.lexId === OTHERS && (
                                        <Input
                                            title={i18n.name}
                                            value={doc.otherDocName}
                                            onChange={v => onChangeDocument({ otherDocName: v })}
                                            obligatory
                                        />
                                    ) || (
                                        <Select
                                            label={i18n.refDoc}
                                            value={doc.lexId}
                                            options={documents}
                                            disabled
                                        />
                                    )
                                }
                            </Grid2>
                            <Grid2 size={6}>
                                <ToggleButtonGroup
                                    buttons={[
                                        { value: DOC_STATE.EXISTING, color: PASTEL_COLOR.green, icon: 'check_value', name: i18n.existing },
                                        { value: DOC_STATE.ABSENT, color: PASTEL_COLOR.red, icon: 'clear', name: i18n.absent },
                                        { value: DOC_STATE.NOTSPECIFIED, color: mediumGrey, icon: 'question_mark', name: i18n.notSpecified },
                                    ]}
                                    value={doc.existing ? DOC_STATE.EXISTING : doc.existing === false ? DOC_STATE.ABSENT : DOC_STATE.NOTSPECIFIED}
                                    onChange={(value) => {
                                        if (value === DOC_STATE.EXISTING) onChangeDocument({ existing: true })
                                        if (value === DOC_STATE.ABSENT) onChangeDocument({ existing: false })
                                        if (value === DOC_STATE.NOTSPECIFIED) onChangeDocument({ existing: undefined })
                                    }}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <SimpleDatePicker
                                    label={i18n.date}
                                    value={doc.date}
                                    onChange={v => onChangeDocument({ date: v })}
                                />
                            </Grid2>
                            <Grid2 size={6}>
                                <Select
                                    label={i18n.relevance}
                                    value={doc.relevance}
                                    options={relevances}
                                    onChange={v => onChangeDocument({ relevance: v })}
                                />
                            </Grid2>
                            <Grid2 size={12}>
                                <SimpleTextArea
                                    title={i18n.comment}
                                    value={doc.comment}
                                    onChange={v => onChangeDocument({ comment: v })}
                                />
                            </Grid2>
                        </Grid2>
                    </CardContent>
                </Card>
            </DialogContent>
            <DialogActions>
                <Button
                    variant='contained'
                    onClick={() => {
                        if (doc.lexId === OTHERS && !doc.otherDocName) {
                            dispatch(ToastrAction.error(i18n.pleaseCompleteAllRequiredField))
                        } else {
                            onChange({ ...doc, toUpdate: true })
                            setIsOpen(false)
                            setDocument({})
                        }
                    }}
                >
                    {i18n.register}
                </Button>
            </DialogActions>
        </Dialog>
    )
}

DocumentDialog.propTypes = {
    isOpen: PropTypes.bool,
    setIsOpen: PropTypes.func,
    onChange: PropTypes.func,
    selectedDocument: PropTypes.object, // DtoPgsseDocument
}

const headers = ['refDoc', 'existing', 'date', 'relevance', 'comment']

const DocumentsPanel = ({
    link_docs = [],
    onChange,
    readMode,
}) => {
    const dispatch = useDispatch()

    const {
        sandreCodes,
    } = useSelector(store => ({
        sandreCodes: store.ReferencialReducer.sandreCodes,
    }), shallowEqual)

    const [isOpen, setIsOpen] = useState(false)
    const [selectedDocument, setSelectedDocument] = useState({})

    const documents = useSandreList(SANDRE.PGSSE_DOCUMENTS)

    const indexedElements = [
        ...documents.map(act => ({
            ...link_docs.find(doc => doc.lexId === act.code),
            lexId: act.code,
            name: act.name,
        })),
        ...link_docs.filter(d => d.lexId === OTHERS),
    ].map((d, index) => ({ ...d, index }))

    const rows = indexedElements.map((d) => ({
        ...d,
        refDoc: d.name || d.otherDocName,
        existing: { value: d.existing && <Icon>check_value</Icon> || (d.existing === false && <Icon>clear</Icon>) || <Icon>question_mark</Icon>, sortValue: d.existing },
        date: getDate(d.date),
        relevance: getSandreLabel(sandreCodes, SANDRE.CODE_PERTINENCE, d.relevance),
        comment: { value: d.comment && <Tooltip title={d.comment}><Icon>information</Icon></Tooltip>, sortValue: d.comment },
    }))

    const updateElements = (newElement) => {
        const newLinkDocs = (() => {
            if (!hasValue(newElement.index)) {
                return [...indexedElements, newElement]
            }
            return indexedElements.map(d => d.index === newElement.index ? newElement : d)
        })()

        onChange({ link_docs: newLinkDocs.filter(l => l.toUpdate) })
    }

    const actions = compact([{
        icon: 'download',
        tooltip: i18n.export,
        onClick: () => {
            dispatch(ToastrAction.error(i18n.inDeveloppmentFunctionnality))
        },
    }, !readMode && {
        icon: 'note_add',
        tooltip: i18n.add,
        onClick: () => {
            setSelectedDocument({ lexId: OTHERS })
            setIsOpen(true)
        },
    }])

    const lineActions = [{
        icon: 'close',
        onClick: (element) => onChange({ link_docs: indexedElements.filter(l => l.toUpdate && l.index !== element.index) }),
        displayed: !readMode,
    }, {
        icon: 'edit',
        onClick: (element) => {
            setSelectedDocument(indexedElements.find(l => element.index === l.index))
            setIsOpen(true)
        },
        displayed: !readMode,
    }]

    return (
        <>
            <CardTable
                title={i18n.documents}
                rows={rows}
                headers={headers}
                lineActions={lineActions}
                actions={actions}
            />
            <DocumentDialog
                isOpen={isOpen}
                setIsOpen={setIsOpen}
                onChange={updateElements}
                selectedDocument={selectedDocument}
            />
        </>
    )
}

DocumentsPanel.propTypes = {
    link_docs: PropTypes.arrayOf(PropTypes.shape({})),
    onChange: PropTypes.func,
    readMode: PropTypes.bool,
}

export default DocumentsPanel